import { useState, useEffect } from 'react';
import PhoneVerification from './PhoneVerification';
import { useSession } from '../contexts/SessionContext';
import FieldLabel from './FieldLabel';

// Format phone number for display as (xxx) xxx-xxxx
const formatForDisplay = (value) => {
  if (!value) return '';
  
  // Extract just the digits
  let digits = value.replace(/[^\d]/g, '');
  
  // If it starts with 1 and is 11 digits, remove the 1
  if (digits.startsWith('1') && digits.length === 11) {
    digits = digits.substring(1);
  }
  
  // Limit to 10 digits
  digits = digits.slice(0, 10);
  
  const length = digits.length;
  if (length === 0) return '';
  if (length < 4) return digits;
  if (length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const PhoneValidationStep = () => {
  const { sessionId, personalInfo, setPersonalInfo } = useSession();
  const [displayPhone, setDisplayPhone] = useState(() => {
    return personalInfo.phone ? formatForDisplay(personalInfo.phone) : '';
  });

  // Convert phone to international format for storage
  const toInternationalFormat = (value) => {
    if (!value) return '';
    // Remove all non-numeric characters
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length === 10) {
      return '+1' + digits;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return '+' + digits;
    }
    return digits; // Return as-is if not valid
  };

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    const displayValue = formatForDisplay(inputValue);
    setDisplayPhone(displayValue);
    
    // Store in international format
    const internationalValue = toInternationalFormat(inputValue);
    setPersonalInfo(prev => ({ ...prev, phone: internationalValue }));
  };

  // Sync display phone when personalInfo changes
  useEffect(() => {
    setDisplayPhone(formatForDisplay(personalInfo.phone));
  }, [personalInfo.phone]);

  // Check if phone number is complete (10 digits)
  const digits = personalInfo.phone.replace(/[^\d]/g, '');
  const isPhoneComplete = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));

  return (
    <div className="step-content">
      <div className="phone-validation-only">
        <h3 className="section-title">Verify Your Phone Number</h3>
        <p className="section-description">Please enter and verify your phone number to continue with scheduling.</p>
        <div className="form-group">
          {/* CIN-6871: this phone is implicitly required — there is no calendar
              config flag for it; the advance gate in Scheduler.js isStepValid
              case 4 hardcodes a 10/11-digit check for this step. Hence the
              literal required prop rather than a field.required lookup. */}
          <FieldLabel htmlFor="phoneNumber" required>
            Phone Number
          </FieldLabel>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            aria-required="true"
            value={displayPhone}
            onChange={handlePhoneChange}
            placeholder="(555) 123-4567"
            className="phone-input-large"
          />
        </div>
        {isPhoneComplete && sessionId && (
          <PhoneVerification
            phoneNumber={personalInfo.phone}
            sessionId={sessionId}
          />
        )}
      </div>
    </div>
  );
};

export default PhoneValidationStep;